import React, { useState, useRef, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { BackButton } from '../components/ui/back-button';
import { User, Mail, Dices, MessageSquare, Award, Lock, Save, Camera, Phone, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, getUploadUrl } from '../services/api';
import { toast } from 'sonner';

const Profile = () => {
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const fileInputRef = useRef(null);
  
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    bio: user?.profile?.bio || '',
    favorite_games: user?.profile?.favorite_games?.join(', ') || '',
    discord_username: user?.profile?.discord_username || '',
    experience_level: user?.profile?.experience_level || '',
    phone: user?.profile?.phone || '',
    address: user?.profile?.address || ''
  });

  // Update form when user data changes (e.g., after checkAuth)
  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        bio: user.profile?.bio || '',
        favorite_games: user.profile?.favorite_games?.join(', ') || '',
        discord_username: user.profile?.discord_username || '',
        experience_level: user.profile?.experience_level || '',
        phone: user.profile?.phone || '',
        address: user.profile?.address || ''
      });
    }
  }, [user]);

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const getAvatarUrl = (avatar) => getUploadUrl(avatar);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non supporté. Utilisez JPG, PNG, GIF ou WebP.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Maximum 5 Mo.');
      return;
    }

    setUploadingAvatar(true);
    try {
      await authAPI.uploadAvatar(file);
      await checkAuth();
      toast.success('Avatar mis à jour !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        name: profileForm.name,
        bio: profileForm.bio || null,
        favorite_games: profileForm.favorite_games ? profileForm.favorite_games.split(',').map(g => g.trim()).filter(g => g) : null,
        discord_username: profileForm.discord_username || null,
        experience_level: profileForm.experience_level || null,
        phone: profileForm.phone || null,
        address: profileForm.address || null
      };

      await authAPI.updateProfile(updateData);
      await checkAuth();
      toast.success('Profil mis à jour !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);

    try {
      await authAPI.changePassword(passwordForm.current_password, passwordForm.new_password);
      toast.success('Mot de passe modifié !');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { label: 'Administrateur', className: 'bg-purple-600 text-white' },
      member: { label: 'Membre', className: 'bg-green-600 text-white' },
      guest: { label: 'Invité', className: 'bg-gray-600 text-white' }
    };
    return badges[role] || badges.guest;
  };

  const experienceLevels = [
    { value: '', label: 'Non spécifié' },
    { value: 'debutant', label: 'Débutant - Je découvre le JDR' },
    { value: 'intermediaire', label: 'Intermédiaire - Quelques parties à mon actif' },
    { value: 'expert', label: 'Expert - Vétéran des tables' }
  ];

  return (
    <div className="min-h-screen pt-24 px-4 pb-12">
      <div className="container mx-auto max-w-4xl">
        {/* Back Button */}
        <BackButton />
        
        {/* Header with Avatar Upload */}
        <div className="mb-8 text-center">
          <div className="relative inline-block">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAvatarChange}
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
            />
            <div 
              onClick={handleAvatarClick}
              className="w-24 h-24 mx-auto mb-4 rounded-full border-4 border-purple-500/50 cursor-pointer relative overflow-hidden group"
            >
              {user?.avatar ? (
                <img 
                  src={getAvatarUrl(user.avatar)} 
                  alt={user.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center text-4xl font-bold text-white">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {uploadingAvatar ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                ) : (
                  <Camera className="h-8 w-8 text-white" />
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-2">Cliquez pour changer</p>
          </div>
          <h1 className="text-3xl font-bold text-purple-300">{user?.name}</h1>
          <p className="text-gray-400">{user?.email}</p>
          <Badge className={`mt-2 ${getRoleBadge(user?.role).className}`}>
            {getRoleBadge(user?.role).label}
          </Badge>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="bg-purple-900/30 rounded-lg p-1 border border-purple-500/30">
            <Button
              variant={activeTab === 'profile' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('profile')}
              className={activeTab === 'profile' ? 'bg-purple-600' : 'text-purple-300 hover:text-white'}
            >
              <User className="mr-2 h-4 w-4" />
              Mon Profil
            </Button>
            {user?.auth_provider === 'local' && (
              <Button
                variant={activeTab === 'password' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('password')}
                className={activeTab === 'password' ? 'bg-purple-600' : 'text-purple-300 hover:text-white'}
              >
                <Lock className="mr-2 h-4 w-4" />
                Mot de passe
              </Button>
            )}
          </div>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-8">
            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-purple-300 flex items-center">
                    <User className="mr-2 h-4 w-4" />
                    Nom
                  </Label>
                  <Input
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    className="bg-black/50 border-purple-500/30 text-white"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-purple-300 flex items-center">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Discord
                  </Label>
                  <Input
                    value={profileForm.discord_username}
                    onChange={(e) => setProfileForm({ ...profileForm, discord_username: e.target.value })}
                    placeholder="VotreNom#1234"
                    className="bg-black/50 border-purple-500/30 text-white placeholder:text-gray-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-purple-300 flex items-center">
                  <Award className="mr-2 h-4 w-4" />
                  Niveau d'expérience
                </Label>
                <select
                  value={profileForm.experience_level}
                  onChange={(e) => setProfileForm({ ...profileForm, experience_level: e.target.value })}
                  className="w-full bg-black/50 border border-purple-500/30 text-white rounded-md px-3 py-2 focus:border-purple-400 focus:outline-none"
                >
                  {experienceLevels.map(level => (
                    <option key={level.value} value={level.value} className="bg-gray-900">
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-purple-300 flex items-center">
                  <Dices className="mr-2 h-4 w-4" />
                  Jeux de rôle préférés
                </Label>
                <Input
                  value={profileForm.favorite_games}
                  onChange={(e) => setProfileForm({ ...profileForm, favorite_games: e.target.value })}
                  placeholder="D&D 5e, Call of Cthulhu, Vampire (séparez par des virgules)"
                  className="bg-black/50 border-purple-500/30 text-white placeholder:text-gray-500"
                />
                <p className="text-xs text-gray-500">Séparez les jeux par des virgules</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-purple-300 flex items-center">
                    <Phone className="mr-2 h-4 w-4" />
                    Téléphone
                  </Label>
                  <Input
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    placeholder="06 12 34 56 78"
                    className="bg-black/50 border-purple-500/30 text-white placeholder:text-gray-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-purple-300 flex items-center">
                    <MapPin className="mr-2 h-4 w-4" />
                    Adresse postale
                  </Label>
                  <Input
                    value={profileForm.address}
                    onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                    placeholder="123 rue Example, 75000 Paris"
                    className="bg-black/50 border-purple-500/30 text-white placeholder:text-gray-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-purple-300">Bio</Label>
                <Textarea
                  value={profileForm.bio}
                  onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                  placeholder="Parlez-nous de vous, de votre expérience en JDR, vos personnages préférés..."
                  className="bg-black/50 border-purple-500/30 text-white placeholder:text-gray-500 min-h-[120px]"
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold py-6 neon-button"
              >
                <Save className="mr-2 h-5 w-5" />
                {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </form>
          </Card>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && user?.auth_provider === 'local' && (
          <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-8">
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-purple-300">Mot de passe actuel</Label>
                <Input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  className="bg-black/50 border-purple-500/30 text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-purple-300">Nouveau mot de passe</Label>
                <Input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  className="bg-black/50 border-purple-500/30 text-white"
                  required
                  minLength={8}
                />
                <p className="text-xs text-gray-500">Minimum 8 caractères</p>
              </div>

              <div className="space-y-2">
                <Label className="text-purple-300">Confirmer le nouveau mot de passe</Label>
                <Input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  className="bg-black/50 border-purple-500/30 text-white"
                  required
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold py-6 neon-button"
              >
                <Lock className="mr-2 h-5 w-5" />
                {loading ? 'Modification...' : 'Modifier le mot de passe'}
              </Button>
            </form>
          </Card>
        )}

        {/* Info for Google users */}
        {activeTab === 'password' && user?.auth_provider === 'google' && (
          <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-8 text-center">
            <Mail className="h-12 w-12 text-purple-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-purple-300 mb-2">Compte Google</h3>
            <p className="text-gray-400">
              Votre compte utilise l'authentification Google. 
              Vous n'avez pas de mot de passe à gérer ici.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Profile;
